import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Search,
  CreditCard,
  Settings,
  AlertCircle,
  Edit2,
  Trash2,
  Save,
  X,
} from 'lucide-react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  AlertMessage,
  Toast,
  ToastType,
  ModernInput,
  ModernTextarea,
  ModernSelect,
  Switch,
} from '../../components/ui'
import { Button } from '../../components/ui'
import { apiService } from '../../services/api'

interface PaymentForm {
  id: string
  name: string
  description?: string
  isFinancing?: boolean
  isCashPayment?: boolean
  createdAt: string
  updatedAt: string
}

interface PaymentOption {
  id: string
  paymentFormId: string
  name: string
  description?: string
  installments: number
  interestRate?: number
  createdAt: string
  updatedAt: string
}

interface PaymentFormFormData {
  name: string
  description?: string
  isFinancing: boolean
  isCashPayment: boolean
}

interface PaymentOptionFormData {
  paymentFormId: string
  name: string
  description?: string
  installments: number
  interestRate?: number
}

const EMPTY_FORM_DATA: PaymentFormFormData = {
  name: '',
  description: '',
  isFinancing: false,
  isCashPayment: false,
}
const EMPTY_OPTION_DATA: PaymentOptionFormData = {
  paymentFormId: '',
  name: '',
  description: '',
  installments: 1,
  interestRate: 0,
}

export const PaymentFormsPage: React.FC = () => {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState<'forms' | 'options'>('forms')

  // Payment Forms
  const [paymentForms, setPaymentForms] = useState<PaymentForm[]>([])
  const [isLoadingForms, setIsLoadingForms] = useState(false)
  const [errorForms, setErrorForms] = useState<string | null>(null)
  const [searchTermForms, setSearchTermForms] = useState('')
  const [showNewForm, setShowNewForm] = useState(false)
  const [newFormData, setNewFormData] = useState<PaymentFormFormData>(EMPTY_FORM_DATA)
  const [editingFormId, setEditingFormId] = useState<string | null>(null)
  const [editingFormData, setEditingFormData] = useState<PaymentFormFormData>(EMPTY_FORM_DATA)
  const [isSubmittingForm, setIsSubmittingForm] = useState(false)

  // Payment Options
  const [paymentOptions, setPaymentOptions] = useState<PaymentOption[]>([])
  const [isLoadingOptions, setIsLoadingOptions] = useState(false)
  const [errorOptions, setErrorOptions] = useState<string | null>(null)
  const [searchTermOptions, setSearchTermOptions] = useState('')
  const [showNewOption, setShowNewOption] = useState(false)
  const [newOptionData, setNewOptionData] = useState<PaymentOptionFormData>(EMPTY_OPTION_DATA)
  const [editingOptionId, setEditingOptionId] = useState<string | null>(null)
  const [editingOptionData, setEditingOptionData] = useState<PaymentOptionFormData>(EMPTY_OPTION_DATA)
  const [isSubmittingOption, setIsSubmittingOption] = useState(false)

  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchPaymentForms = async () => {
    setIsLoadingForms(true)
    setErrorForms(null)
    try {
      const response = await apiService.getPaymentForms()
      if (Array.isArray(response)) {
        const term = searchTermForms.toLowerCase()
        setPaymentForms(
          term
            ? response.filter(
                f =>
                  f.name.toLowerCase().includes(term) ||
                  (f.description && f.description.toLowerCase().includes(term))
              )
            : response
        )
      } else {
        setPaymentForms([])
      }
    } catch (err) {
      console.error('Erro ao carregar formas de pagamento:', err)
      setErrorForms('Erro ao carregar formas de pagamento. Tente novamente.')
      setPaymentForms([])
    } finally {
      setIsLoadingForms(false)
    }
  }

  const fetchPaymentOptions = async () => {
    setIsLoadingOptions(true)
    setErrorOptions(null)
    try {
      const response = await apiService.getPaymentOptions()
      if (Array.isArray(response)) {
        const term = searchTermOptions.toLowerCase()
        setPaymentOptions(
          term
            ? response.filter(
                o =>
                  o.name.toLowerCase().includes(term) ||
                  (o.description && o.description.toLowerCase().includes(term))
              )
            : response
        )
      } else {
        setPaymentOptions([])
      }
    } catch (err) {
      console.error('Erro ao carregar opções de pagamento:', err)
      setErrorOptions('Erro ao carregar opções de pagamento. Tente novamente.')
      setPaymentOptions([])
    } finally {
      setIsLoadingOptions(false)
    }
  }

  useEffect(() => { fetchPaymentForms() }, [searchTermForms])
  useEffect(() => { fetchPaymentOptions() }, [searchTermOptions])

  // ── Payment Form handlers ──────────────────────────────────────────────────

  const handleCreateForm = async () => {
    if (!newFormData.name.trim()) return
    setIsSubmittingForm(true)
    try {
      await apiService.createPaymentForm(newFormData)
      setToast({ message: 'Forma de pagamento criada com sucesso!', type: 'success' })
      setShowNewForm(false)
      setNewFormData(EMPTY_FORM_DATA)
      fetchPaymentForms()
    } catch (err) {
      console.error(err)
      const status =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'status' in err.response
          ? (err.response as { status: number }).status
          : undefined
      setToast({
        message:
          status === 400
            ? t('settings.paymentForms.exclusiveConflict')
            : 'Erro ao criar forma de pagamento.',
        type: 'error',
      })
    } finally {
      setIsSubmittingForm(false)
    }
  }

  const handleStartEditForm = (form: PaymentForm) => {
    setEditingFormId(form.id)
    setEditingFormData({
      name: form.name,
      description: form.description || '',
      isFinancing: form.isFinancing ?? false,
      isCashPayment: form.isCashPayment ?? false,
    })
  }

  const handleUpdateForm = async (id: string) => {
    if (!editingFormData.name.trim()) return
    setIsSubmittingForm(true)
    try {
      await apiService.updatePaymentForm(id, editingFormData)
      setToast({ message: 'Forma de pagamento atualizada!', type: 'success' })
      setEditingFormId(null)
      fetchPaymentForms()
    } catch (err) {
      console.error(err)
      const status =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'status' in err.response
          ? (err.response as { status: number }).status
          : undefined
      setToast({
        message:
          status === 400
            ? t('settings.paymentForms.exclusiveConflict')
            : 'Erro ao atualizar forma de pagamento.',
        type: 'error',
      })
    } finally {
      setIsSubmittingForm(false)
    }
  }

  const handleDeleteForm = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta forma de pagamento?')) return
    try {
      await apiService.deletePaymentForm(id)
      setToast({ message: 'Forma de pagamento excluída!', type: 'success' })
      fetchPaymentForms()
    } catch (err) {
      console.error(err)
      setToast({ message: 'Erro ao excluir forma de pagamento.', type: 'error' })
    }
  }

  // ── Payment Option handlers ────────────────────────────────────────────────

  const handleCreateOption = async () => {
    if (!newOptionData.name.trim() || !newOptionData.paymentFormId) return
    setIsSubmittingOption(true)
    try {
      await apiService.createPaymentOption(newOptionData)
      setToast({ message: 'Opção de pagamento criada com sucesso!', type: 'success' })
      setShowNewOption(false)
      setNewOptionData(EMPTY_OPTION_DATA)
      fetchPaymentOptions()
    } catch (err) {
      console.error(err)
      setToast({ message: 'Erro ao criar opção de pagamento.', type: 'error' })
    } finally {
      setIsSubmittingOption(false)
    }
  }

  const handleStartEditOption = (option: PaymentOption) => {
    setEditingOptionId(option.id)
    setEditingOptionData({
      paymentFormId: option.paymentFormId,
      name: option.name,
      description: option.description || '',
      installments: option.installments,
      interestRate: option.interestRate || 0,
    })
  }

  const handleUpdateOption = async (id: string) => {
    if (!editingOptionData.name.trim() || !editingOptionData.paymentFormId) return
    setIsSubmittingOption(true)
    try {
      await apiService.updatePaymentOption(id, editingOptionData)
      setToast({ message: 'Opção de pagamento atualizada!', type: 'success' })
      setEditingOptionId(null)
      fetchPaymentOptions()
    } catch (err) {
      console.error(err)
      setToast({ message: 'Erro ao atualizar opção de pagamento.', type: 'error' })
    } finally {
      setIsSubmittingOption(false)
    }
  }

  const handleDeleteOption = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta opção de pagamento?')) return
    try {
      await apiService.deletePaymentOption(id)
      setToast({ message: 'Opção de pagamento excluída!', type: 'success' })
      fetchPaymentOptions()
    } catch (err) {
      console.error(err)
      setToast({ message: 'Erro ao excluir opção de pagamento.', type: 'error' })
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Formas e Opções de Pagamento</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Gerencie as formas de pagamento e opções de financiamento disponíveis nas propostas
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="-mb-px flex space-x-8">
          {(['forms', 'options'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab
                  ? 'border-accent text-accent'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {tab === 'forms' ? 'Formas de Pagamento' : 'Opções de Pagamento'}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Tab: Formas de Pagamento ─────────────────────────────────────── */}
      {activeTab === 'forms' && (
        <div className="space-y-4">
          {errorForms && <AlertMessage variant="error" message={errorForms} />}
          {isLoadingForms && <AlertMessage variant="loading" message="Carregando formas de pagamento..." />}

          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                placeholder="Pesquisar formas de pagamento..."
                value={searchTermForms}
                onChange={e => setSearchTermForms(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
              />
            </div>
            <Button
              onClick={() => { setShowNewForm(true); setEditingFormId(null) }}
              disabled={showNewForm}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Forma de Pagamento
            </Button>
          </div>

          {/* New form card */}
          {showNewForm && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <CreditCard className="h-4 w-4 text-accent" />
                  Nova Forma de Pagamento
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ModernInput
                  label="Nome"
                  value={newFormData.name}
                  onChange={e => setNewFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="Ex: À vista, Financiamento..."
                  required
                />
                <ModernTextarea
                  label="Descrição"
                  value={newFormData.description}
                  onChange={e => setNewFormData(p => ({ ...p, description: e.target.value }))}
                  placeholder="Descrição opcional"
                  rows={2}
                />
                <p className="text-xs text-muted-foreground">{t('settings.paymentForms.kindHint')}</p>
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Switch
                      checked={newFormData.isFinancing}
                      onCheckedChange={(v) =>
                        setNewFormData((p) => ({
                          ...p,
                          isFinancing: v,
                          isCashPayment: v ? false : p.isCashPayment,
                        }))
                      }
                    />
                    <span className="text-sm text-foreground">{t('settings.paymentForms.isFinancing')}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Switch
                      checked={newFormData.isCashPayment}
                      onCheckedChange={(v) =>
                        setNewFormData((p) => ({
                          ...p,
                          isCashPayment: v,
                          isFinancing: v ? false : p.isFinancing,
                        }))
                      }
                    />
                    <span className="text-sm text-foreground">{t('settings.paymentForms.isCashPayment')}</span>
                  </label>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button onClick={handleCreateForm} disabled={isSubmittingForm || !newFormData.name.trim()}>
                    <Save className="h-4 w-4 mr-2" />
                    {isSubmittingForm ? 'Salvando...' : 'Salvar'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setShowNewForm(false); setNewFormData(EMPTY_FORM_DATA) }}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancelar
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* List */}
          <div className="grid gap-3">
            {paymentForms.map(form => (
              <Card key={form.id}>
                <CardContent className="p-4">
                  {editingFormId === form.id ? (
                    <div className="space-y-4">
                      <ModernInput
                        label="Nome"
                        value={editingFormData.name}
                        onChange={e => setEditingFormData(p => ({ ...p, name: e.target.value }))}
                        required
                      />
                      <ModernTextarea
                        label="Descrição"
                        value={editingFormData.description}
                        onChange={e => setEditingFormData(p => ({ ...p, description: e.target.value }))}
                        rows={2}
                      />
                      <p className="text-xs text-muted-foreground">{t('settings.paymentForms.kindHint')}</p>
                      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Switch
                            checked={editingFormData.isFinancing}
                            onCheckedChange={(v) =>
                              setEditingFormData((p) => ({
                                ...p,
                                isFinancing: v,
                                isCashPayment: v ? false : p.isCashPayment,
                              }))
                            }
                          />
                          <span className="text-sm text-foreground">{t('settings.paymentForms.isFinancing')}</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Switch
                            checked={editingFormData.isCashPayment}
                            onCheckedChange={(v) =>
                              setEditingFormData((p) => ({
                                ...p,
                                isCashPayment: v,
                                isFinancing: v ? false : p.isFinancing,
                              }))
                            }
                          />
                          <span className="text-sm text-foreground">{t('settings.paymentForms.isCashPayment')}</span>
                        </label>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleUpdateForm(form.id)}
                          disabled={isSubmittingForm || !editingFormData.name.trim()}
                        >
                          <Save className="h-4 w-4 mr-2" />
                          {isSubmittingForm ? 'Salvando...' : 'Salvar'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => { setEditingFormId(null); setEditingFormData(EMPTY_FORM_DATA) }}
                        >
                          <X className="h-4 w-4 mr-2" />
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CreditCard className="h-4 w-4 text-accent" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-semibold text-foreground text-sm">{form.name}</p>
                            {form.isFinancing && (
                              <span className="text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-accent/15 text-accent">
                                {t('settings.paymentForms.badgeFinancing')}
                              </span>
                            )}
                            {form.isCashPayment && (
                              <span className="text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                {t('settings.paymentForms.badgeCash')}
                              </span>
                            )}
                          </div>
                          {form.description && (
                            <p className="text-xs text-muted-foreground mt-0.5">{form.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleStartEditForm(form)}
                          className="p-1.5 rounded-md text-muted-foreground hover:text-accent hover:bg-accent/10 transition-colors"
                          title="Editar"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteForm(form.id)}
                          className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                          title="Excluir"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}

            {paymentForms.length === 0 && !isLoadingForms && !showNewForm && (
              <Card>
                <CardContent className="p-10 text-center">
                  <AlertCircle className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <p className="font-medium text-foreground text-sm">Nenhuma forma de pagamento cadastrada</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Clique em "Nova Forma de Pagamento" para começar.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: Opções de Pagamento ─────────────────────────────────────── */}
      {activeTab === 'options' && (
        <div className="space-y-4">
          {errorOptions && <AlertMessage variant="error" message={errorOptions} />}
          {isLoadingOptions && <AlertMessage variant="loading" message="Carregando opções de pagamento..." />}

          {paymentForms.length === 0 && (
            <AlertMessage
              variant="info"
              message="Você precisa criar pelo menos uma forma de pagamento antes de adicionar opções."
            />
          )}

          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                placeholder="Pesquisar opções de pagamento..."
                value={searchTermOptions}
                onChange={e => setSearchTermOptions(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent transition-colors"
              />
            </div>
            <Button
              onClick={() => {
                setShowNewOption(true)
                setEditingOptionId(null)
                setNewOptionData({
                  ...EMPTY_OPTION_DATA,
                  paymentFormId: paymentForms[0]?.id || '',
                })
              }}
              disabled={showNewOption || paymentForms.length === 0}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Opção de Pagamento
            </Button>
          </div>

          {/* New option card */}
          {showNewOption && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Settings className="h-4 w-4 text-accent" />
                  Nova Opção de Pagamento
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ModernSelect
                  label="Forma de Pagamento"
                  value={newOptionData.paymentFormId}
                  onChange={e => setNewOptionData(p => ({ ...p, paymentFormId: e.target.value }))}
                  options={[
                    { value: '', label: 'Selecione...' },
                    ...paymentForms.map(f => ({ value: f.id, label: f.name })),
                  ]}
                  required
                />
                <ModernInput
                  label="Nome"
                  value={newOptionData.name}
                  onChange={e => setNewOptionData(p => ({ ...p, name: e.target.value }))}
                  placeholder="Ex: Banco Bradesco, 36x"
                  required
                />
                <ModernTextarea
                  label="Descrição"
                  value={newOptionData.description}
                  onChange={e => setNewOptionData(p => ({ ...p, description: e.target.value }))}
                  placeholder="Descrição opcional"
                  rows={2}
                />
                <div className="grid grid-cols-2 gap-4">
                  <ModernInput
                    label="Parcelas"
                    type="number"
                    value={newOptionData.installments}
                    onChange={e => setNewOptionData(p => ({ ...p, installments: parseInt(e.target.value) || 1 }))}
                    min="1"
                    required
                  />
                  <ModernInput
                    label="Taxa de Juros (%)"
                    type="number"
                    step="0.01"
                    value={newOptionData.interestRate}
                    onChange={e => setNewOptionData(p => ({ ...p, interestRate: parseFloat(e.target.value) || 0 }))}
                    min="0"
                    placeholder="0.00"
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button
                    onClick={handleCreateOption}
                    disabled={isSubmittingOption || !newOptionData.name.trim() || !newOptionData.paymentFormId}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {isSubmittingOption ? 'Salvando...' : 'Salvar'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setShowNewOption(false); setNewOptionData(EMPTY_OPTION_DATA) }}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancelar
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* List */}
          <div className="grid gap-3">
            {paymentOptions.map(option => {
              const form = paymentForms.find(f => f.id === option.paymentFormId)
              return (
                <Card key={option.id}>
                  <CardContent className="p-4">
                    {editingOptionId === option.id ? (
                      <div className="space-y-4">
                        <ModernSelect
                          label="Forma de Pagamento"
                          value={editingOptionData.paymentFormId}
                          onChange={e => setEditingOptionData(p => ({ ...p, paymentFormId: e.target.value }))}
                          options={[
                            { value: '', label: 'Selecione...' },
                            ...paymentForms.map(f => ({ value: f.id, label: f.name })),
                          ]}
                          required
                        />
                        <ModernInput
                          label="Nome"
                          value={editingOptionData.name}
                          onChange={e => setEditingOptionData(p => ({ ...p, name: e.target.value }))}
                          required
                        />
                        <ModernTextarea
                          label="Descrição"
                          value={editingOptionData.description}
                          onChange={e => setEditingOptionData(p => ({ ...p, description: e.target.value }))}
                          rows={2}
                        />
                        <div className="grid grid-cols-2 gap-4">
                          <ModernInput
                            label="Parcelas"
                            type="number"
                            value={editingOptionData.installments}
                            onChange={e => setEditingOptionData(p => ({ ...p, installments: parseInt(e.target.value) || 1 }))}
                            min="1"
                            required
                          />
                          <ModernInput
                            label="Taxa de Juros (%)"
                            type="number"
                            step="0.01"
                            value={editingOptionData.interestRate}
                            onChange={e => setEditingOptionData(p => ({ ...p, interestRate: parseFloat(e.target.value) || 0 }))}
                            min="0"
                            placeholder="0.00"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleUpdateOption(option.id)}
                            disabled={isSubmittingOption || !editingOptionData.name.trim() || !editingOptionData.paymentFormId}
                          >
                            <Save className="h-4 w-4 mr-2" />
                            {isSubmittingOption ? 'Salvando...' : 'Salvar'}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => { setEditingOptionId(null); setEditingOptionData(EMPTY_OPTION_DATA) }}
                          >
                            <X className="h-4 w-4 mr-2" />
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Settings className="h-4 w-4 text-accent" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-foreground text-sm">{option.name}</p>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              {form && (
                                <span className="text-xs text-muted-foreground bg-surface border border-border px-1.5 py-0.5 rounded">
                                  {form.name}
                                </span>
                              )}
                              <span className="text-xs text-muted-foreground bg-surface border border-border px-1.5 py-0.5 rounded">
                                {option.installments}x
                              </span>
                              {option.interestRate ? (
                                <span className="text-xs text-muted-foreground bg-surface border border-border px-1.5 py-0.5 rounded">
                                  {option.interestRate}% a.m.
                                </span>
                              ) : null}
                            </div>
                            {option.description && (
                              <p className="text-xs text-muted-foreground mt-1">{option.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => handleStartEditOption(option)}
                            className="p-1.5 rounded-md text-muted-foreground hover:text-accent hover:bg-accent/10 transition-colors"
                            title="Editar"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleDeleteOption(option.id)}
                            className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                            title="Excluir"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )
            })}

            {paymentOptions.length === 0 && !isLoadingOptions && !showNewOption && (
              <Card>
                <CardContent className="p-10 text-center">
                  <AlertCircle className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <p className="font-medium text-foreground text-sm">Nenhuma opção de pagamento cadastrada</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {paymentForms.length === 0
                      ? 'Crie primeiro uma forma de pagamento.'
                      : 'Clique em "Nova Opção de Pagamento" para começar.'}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} duration={3000} />
      )}
    </div>
  )
}
